import { FLOTABOT } from "../components/layout/secciones";
import { abrirSesionEnFlotaBot } from "./ssoFlotaBot";

/* Traer la planilla del Drive, desde el hub.
 *
 * ── POR QUÉ ESTO VIVE ACÁ SI EL TRABAJO LO HACE FLOTABOT ──────────────────
 *
 * El hub es React estático: no tiene servidor, así que no puede bajar una
 * planilla del Drive ni escribir con la clave de servicio. FlotaBot sí, y ya lo
 * hacía — `/api/sync-docs` existe desde hace rato.
 *
 * EL PROBLEMA ERA QUE EL BOTÓN ESTABA EN LA PANTALLA EQUIVOCADA. Vivía en
 * `/documentos` del panel de FlotaBot, y el menú del hub dice «Documentos» y
 * abre ESTA pantalla, que es otra. O sea que la única forma de actualizar la
 * documentación de 219 choferes era escribir a mano una URL que nadie tiene a
 * mano. No fallaba nada: simplemente no lo corría nunca nadie, y el síntoma
 * era esta tabla mostrando DNIs en vez de nombres.
 *
 * Es la misma falla que ya tuvimos al revés, con `/api/sync-pins`: una ruta sin
 * botón. Un botón que no está donde se hace el trabajo es lo mismo que no estar.
 *
 * ── LA SESIÓN SE ABRE SOLA ────────────────────────────────────────────────
 *
 * FlotaBot tiene su propia cookie. Se canjea el token de Supabase por ella con
 * el mismo `/sso` que ya usan los paneles embebidos — y se hace ANTES de cada
 * llamada, no una vez al cargar la pantalla: la cookie se vence, y un 401
 * después de veinte segundos de espera es la peor forma de enterarse.
 *
 * `credentials: "include"` es lo que hace que el navegador mande esa cookie.
 * Sin eso el pedido sale, contesta 401, y parece un problema de permisos.
 */

/* La respuesta siempre tiene la misma forma, tenga éxito o no:
 *
 *   { ok, mensaje, detalle }
 *
 * `ok` false con `mensaje` es lo que la pantalla muestra. NO se distingue por
 * la ausencia de campos: un `undefined` que la pantalla tenga que interpretar
 * termina en un `if` distinto en cada botón.
 */
async function llamar(ruta, { queNoPuedo }) {
  const sesion = await abrirSesionEnFlotaBot();
  if (!sesion.ok) {
    return {
      ok: false,
      mensaje:
        sesion.motivo === "sin-sesion"
          ? "Se cerró la sesión. Entrá de nuevo y probá otra vez."
          : "No se pudo hablar con FlotaBot. Puede estar reiniciando; probá en un minuto.",
    };
  }

  let r;
  try {
    r = await fetch(`${FLOTABOT}${ruta}`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* Falla de red o CORS. Para el que aprieta son lo mismo —no pasó nada— y
     * decirle «error de CORS» lo manda a buscar donde no puede arreglarlo. */
    return { ok: false, mensaje: "No se pudo conectar con FlotaBot." };
  }

  /* «NO PODÉS» Y «NO PUDE» NO SON LO MISMO, y por la misma puerta salían
   * iguales. Un 403 es el permiso; cualquier otra cosa es una falla del
   * servidor, y mandar a pedir un permiso por un Supabase caído hace que la
   * persona busque el problema donde no está. */
  if (r.status === 403) return { ok: false, mensaje: queNoPuedo };
  if (r.status === 401) {
    return {
      ok: false,
      mensaje: "FlotaBot no reconoció la sesión. Recargá la página y probá otra vez.",
    };
  }

  let cuerpo = null;
  try {
    cuerpo = await r.json();
  } catch {
    cuerpo = null;
  }
  if (!r.ok || !cuerpo) {
    return { ok: false, mensaje: `FlotaBot contestó ${r.status}.` };
  }
  return cuerpo;
}

/* Baja la planilla y la vuelca a Redis y a `chofer_documentos`.
 *
 * Se puede repetir sin miedo: el vuelco usa `coalesce`, o sea que completa
 * huecos y NO pisa lo que se corrigió desde esta pantalla. Al revés, cada
 * sincronización borraría las correcciones y el síntoma sería «los datos se
 * desarreglan solos».
 */
export async function sincronizarPlanilla() {
  const r = await llamar("/api/sync-docs", {
    queNoPuedo: "No podés sincronizar la planilla.",
  });
  if (r.mensaje) return r;
  /* El vuelco al hub va aparte del sync, y si falló SE DICE: sin eso la
   * pantalla mostraría lo mismo de antes y no habría por qué sospecharlo. */
  return {
    ok: Boolean(r.ok),
    mensaje: r.message || (r.ok ? "Listo." : "No se pudo sincronizar."),
    detalle: r.hub || null,
  };
}

/* Los PIN van por su propia puerta y no como un paso de la sincronización.
 *
 * Mueven una credencial de pago: tiene que ser algo que alguien decide hacer,
 * no un efecto de apretar «Sincronizar». Piden `ver.pin_ypf`, el mismo permiso
 * que hace falta para verlos — quien no puede mirar un PIN tampoco tiene por
 * qué poder moverlos en bloque.
 *
 * La respuesta trae cuántos entraron y qué DNIs quedaron a revisar. **Nunca un
 * PIN**, ni siquiera cuando algo falla.
 */
export async function importarPins() {
  const r = await llamar("/api/sync-pins", {
    queNoPuedo: "No podés importar los PIN.",
  });
  if (r.mensaje) return r;
  /* LA CLAVE ES `cargados` Y NO OTRA. Acá había tres nombres inventados
   * (`subidos`, `ok_count`, `cantidad`) y el servidor manda `cargados`, así que
   * el número no salía nunca y el mensaje quedaba en «PIN importados.» a secas.
   * Al lado de «20 a revisar», el único número de la frase era el de los que
   * NO entraron — y se leyó, con toda razón, como que sólo habían entrado 20.
   *
   * Un mensaje con un solo número es un mensaje que dice ese número. */
  const n = typeof r.cargados === "number" ? r.cargados : null;
  const revisar = Array.isArray(r.a_revisar) ? r.a_revisar.length : 0;
  const sinPin = typeof r.sin_pin === "number" ? r.sin_pin : null;
  return {
    ok: Boolean(r.ok),
    mensaje: r.ok
      ? [
          n === null ? "PIN importados." : `${n} PIN importados.`,
          /* Los tres números dicen cosas distintas y por eso van los tres:
           *   cargados  · entraron
           *   sin_pin   · la planilla dice que esa persona no tiene
           *   a_revisar · la celda tiene algo que NO es un PIN de 4 dígitos
           * Sin el primero, los otros dos se leen como el total. */
          sinPin ? `${sinPin} sin PIN en la planilla.` : "",
          revisar ? `${revisar} con la celda mal cargada, a revisar.` : "",
        ]
          .filter(Boolean)
          .join(" ")
      : r.error || "No se pudieron importar.",
    detalle: r,
  };
}
